/**
 * Android Auto bridge service.
 * Communicates with native FricampCarPlugin to send spots, position
 * and route data to the car display.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import SunCalc from 'suncalc';
import type { CampingSpot, Coordinates, MapBounds } from '../types';
import { getDistance, getPolylineBbox, isPointNearPolyline } from './geometryUtils';

/**
 * Map a CampingSpot.spotType to a Norwegian human-readable label for the
 * car display. Falls back to attractionCategory or "Plass".
 */
function displayTypeFor(spot: CampingSpot): string {
  switch (spot.spotType) {
    case 'rasteplass': return 'Rasteplass';
    case 'snuplass': return 'Snuplass';
    case 'gapahuk': return 'Gapahuk';
    case 'wildspot': return 'Fricamp-plass';
    case 'commercial_campsite': return 'Campingplass';
    case 'coastal': return 'Kystplass';
    case 'forbidden_area': return 'Forbudt område';
    case 'confirmed': return 'Verifisert plass';
    case 'attraction': return spot.attractionCategory
      ? attractionCategoryLabel(spot.attractionCategory)
      : 'Severdighet';
    case 'osm_amenity':
      // OSM amenities are typically rest areas, picnic sites, viewpoints.
      // The CampingSpot stores the actual amenity kind in features/tags.
      return amenityLabelFromTags(spot.tags, spot.features) || 'Rasteplass';
    case 'algorithmic':
      // Distinguish verified (community-confirmed) from AI-only candidates so
      // the driver can judge trust at a glance. Verified ones got user check-ins
      // or admin confirmation; AI-only are from the discovery algorithm.
      return spot.isVerified ? 'Verifisert plass' : 'AI-beregnet plass';
    default: return 'Plass';
  }
}

function attractionCategoryLabel(cat: string): string {
  switch (cat) {
    case 'viewpoint': return 'Utsiktspunkt';
    case 'waterfall': return 'Foss';
    case 'museum': return 'Museum';
    case 'monument': return 'Monument';
    case 'memorial': return 'Minnesmerke';
    case 'castle': return 'Slott';
    case 'ruins': return 'Ruin';
    case 'beach': return 'Strand';
    case 'cave': return 'Hule';
    case 'peak': return 'Topp';
    default: return cat.charAt(0).toUpperCase() + cat.slice(1);
  }
}

function amenityLabelFromTags(tags?: string[], features?: string[]): string | null {
  const all = [...(tags ?? []), ...(features ?? [])].map(s => s.toLowerCase());
  if (all.includes('picnic_site') || all.includes('picnic')) return 'Piknikplass';
  if (all.includes('parking')) return 'Parkering';
  if (all.includes('viewpoint')) return 'Utsiktspunkt';
  if (all.includes('shelter')) return 'Le-sted';
  if (all.includes('toilets')) return 'Toalett';
  if (all.includes('drinking_water')) return 'Drikkevann';
  if (all.includes('camp_site')) return 'Campingplass';
  return null;
}

interface FricampCarPlugin {
  updateNearbySpots(options: { spots: CarSpotPayload[] }): Promise<void>;
  updatePosition(options: { latitude: number; longitude: number; heading: number; speed: number }): Promise<void>;
  showRoute(options: { route: CarRoutePayload }): Promise<void>;
  clearRoute(): Promise<void>;
  setMapStyle(options: { style: string }): Promise<void>;
  setLanguage(options: { language: string }): Promise<void>;
  /**
   * Tell the native plugin whether the current user has premium access.
   * When `hasAccess` is false, the plugin drops any incoming data writes
   * (defense-in-depth — the TS hook is the primary gate).
   */
  setSubscriptionGate(options: { hasAccess: boolean }): Promise<void>;
  showCheckInPrompt(options: { spotId: string; spotName: string }): Promise<void>;
  checkIn(options: { spotId: string }): Promise<void>;
  isConnected(): Promise<{ connected: boolean }>;
  addListener(event: 'spotSelected', handler: (data: { spotId: string }) => void): Promise<{ remove: () => void }>;
  addListener(event: 'navigateToSpot', handler: (data: { spotId: string }) => void): Promise<{ remove: () => void }>;
  addListener(event: 'checkInConfirmed', handler: (data: { spotId: string }) => void): Promise<{ remove: () => void }>;
  addListener(event: 'mapStyleChanged', handler: (data: { style: string }) => void): Promise<{ remove: () => void }>;
  addListener(event: 'connectionChanged', handler: (data: { connected: boolean }) => void): Promise<{ remove: () => void }>;
  addListener(event: 'syncRequested', handler: () => void): Promise<{ remove: () => void }>;
}

/**
 * Handle map style change request from car.
 */
export function onAutoMapStyleChange(handler: (style: 'topo' | 'satellite') => void): (() => void) | null {
  if (!FricampCar) return null;
  let listener: any;
  FricampCar.addListener('mapStyleChanged', (data) => handler(data.style as any)).then(l => listener = l);
  return () => listener?.remove();
}

/**
 * Handle check-in from car.
 */
export function onAutoCheckIn(handler: (spotId: string) => void): (() => void) | null {
  if (!FricampCar) return null;
  let listener: any;
  FricampCar.addListener('checkInConfirmed', (data) => handler(data.spotId)).then(l => listener = l);
  return () => listener?.remove();
}

/**
 * Handle sync request from car.
 */
export function onAutoSyncRequest(handler: () => void): (() => void) | null {
  if (!FricampCar) return null;
  let listener: any;
  FricampCar.addListener('syncRequested', () => handler()).then(l => listener = l);
  return () => listener?.remove();
}

interface CarSpotPayload {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  score: number;
  spotType: string;
  distanceMeters: number;
  isDriveable: boolean;
  legalStatus: string;
  elevation: number;
  nearestHouse: number;
  hasBarrier: boolean;
  description: string;
  imageUrl: string;
  temperature: number;
  weatherSymbol: string;
  badges: string[];
  // v1.0.198: verified, occupancy and sun-times for the car detail screen
  isVerified: boolean;
  occupiedByName: string;   // empty if not occupied tonight
  occupiedUntilMs: number;  // 0 if not occupied
  sunriseMs: number;        // 0 if outside polar circle / unavailable
  sunsetMs: number;
}

interface CarRoutePayload {
  coordinates: [number, number][];
  distanceMeters: number;
  durationSeconds: number;
  destinationName: string;
  /**
   * Id of the destination spot. Lets the car overlay match the route to a
   * known Fricamp spot (for the destination card) without needing to infer
   * from polyline endpoint. Falls back to inference if missing or unknown.
   */
  destinationSpotId?: string;
  steps?: CarRouteStepPayload[];
}

export interface CarRouteStepPayload {
  maneuverType: string;        // OSRM-style: "turn", "depart", "arrive", "roundabout", etc.
  maneuverModifier: string;    // "left", "right", "straight", "slight left", etc.
  instruction: string;         // Optional human-readable string
  roadName: string;
  distanceMeters: number;
  durationSeconds: number;
  latitude: number;
  longitude: number;
}

const FricampCar = Capacitor.getPlatform() === 'android'
  ? registerPlugin<FricampCarPlugin>('FricampCar')
  : null;

/**
 * Check if Android Auto is currently connected.
 */
export async function isAndroidAutoConnected(): Promise<boolean> {
  if (!FricampCar) return false;
  try {
    const { connected } = await FricampCar.isConnected();
    return connected;
  } catch {
    return false;
  }
}

/**
 * Send nearby spots to Android Auto display.
 *
 * Selection logic (prioritized for 1:1 parity with the phone's visible map):
 *   1. Spots inside the phone's current viewport bounds (so the car shows
 *      exactly what the phone shows — see user feedback "punkter på AA-kart
 *      som ikke vises på mobilen burde være 1-1")
 *   2. Up to 20 spots nearest user (covers the case where phone is off-map
 *      or browsing somewhere else than user position)
 *   3. If route is active: also include up to 30 more spots within 2km of
 *      the route polyline (for "next Fricamp along route" overlay on long
 *      routes whose corridor extends beyond viewport)
 *
 * Deduplicates by id and caps total to 60.
 */
export async function sendSpotsToAuto(
  spots: CampingSpot[],
  userPosition: Coordinates | null,
  routeCoords?: Coordinates[] | null,
  viewBounds?: MapBounds | null,
): Promise<void> {
  console.log(`[AndroidAuto] sendSpotsToAuto entry: ${spots.length} spots, hasPos: ${!!userPosition}, route: ${routeCoords?.length ?? 0}, bounds: ${!!viewBounds}, plugin: ${!!FricampCar}`);
  if (!FricampCar || !userPosition) return;

  const validSpots = spots.filter(s => s.location && !s.isDeleted && !s.isInvalid);

  // Tier 1: viewport spots — what's currently on the phone map
  const inViewport = viewBounds
    ? validSpots.filter(s =>
        s.location.latitude  >= viewBounds.south &&
        s.location.latitude  <= viewBounds.north &&
        s.location.longitude >= viewBounds.west &&
        s.location.longitude <= viewBounds.east
      )
    : [];

  // Tier 2: nearest-user (fallback when viewport is empty or far away)
  const nearestUser = validSpots
    .map(s => ({
      spot: s,
      distance: getDistance(
        userPosition.latitude, userPosition.longitude,
        s.location.latitude, s.location.longitude,
      ),
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 20);

  // Merge: viewport first (with distance), then nearest-user dedup'd
  const seenIds = new Set<string>();
  const combined: { spot: CampingSpot; distance: number }[] = [];
  for (const s of inViewport) {
    if (seenIds.has(s.id)) continue;
    seenIds.add(s.id);
    combined.push({
      spot: s,
      distance: getDistance(userPosition.latitude, userPosition.longitude,
        s.location.latitude, s.location.longitude),
    });
  }
  for (const e of nearestUser) {
    if (seenIds.has(e.spot.id)) continue;
    seenIds.add(e.spot.id);
    combined.push(e);
  }

  // Tier 3: route-corridor spots
  if (routeCoords && routeCoords.length > 1) {
    const bbox = getPolylineBbox(routeCoords);
    const routeSpots = validSpots
      .filter(s => !seenIds.has(s.id))
      .filter(s => isPointNearPolyline(s.location, routeCoords, 2000, bbox))
      .map(s => ({
        spot: s,
        distance: getDistance(
          userPosition.latitude, userPosition.longitude,
          s.location.latitude, s.location.longitude,
        ),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 30);
    for (const e of routeSpots) { seenIds.add(e.spot.id); combined.push(e); }
  }

  const spotsWithDistance = combined.slice(0, 60);
  console.log(`[AndroidAuto] Sending ${spotsWithDistance.length} spots (viewport: ${inViewport.length}, nearest: ${nearestUser.length}, route-corridor: ${combined.length - inViewport.length - nearestUser.length})...`);

  const nowMs = Date.now();
  const payload: CarSpotPayload[] = spotsWithDistance.map(({ spot, distance }) => {
    // Collect images and pick the first one
    const allImages: string[] = [];
    if (spot.photoUrls) allImages.push(...spot.photoUrls);
    if ((spot as any).ugcImages) {
      (spot as any).ugcImages.forEach((img: any) => {
        const url = typeof img === 'string' ? img : img?.url;
        if (url) allImages.push(url);
      });
    }
    if (allImages.length === 0 && spot.auto_image_url) allImages.push(spot.auto_image_url);

    const description = spot.aiResult?.vurdering || spot.aiResult?.juridisk_tolking || spot.reasoning || spot.features?.join(', ') || '';

    const displayType = displayTypeFor(spot);

    // Sun times — computed locally with suncalc. Cheap (no API call).
    let sunriseMs = 0;
    let sunsetMs = 0;
    try {
      const times = SunCalc.getTimes(new Date(), spot.location.latitude, spot.location.longitude);
      if (times.sunrise instanceof Date && !isNaN(times.sunrise.getTime())) {
        sunriseMs = times.sunrise.getTime();
      }
      if (times.sunset instanceof Date && !isNaN(times.sunset.getTime())) {
        sunsetMs = times.sunset.getTime();
      }
    } catch {
      // Polar day/night returns Invalid Date — fine, we just send 0
    }

    // Occupancy — only forward if still active
    const occupiedUntil = (spot.occupiedUntil ?? 0);
    const stillOccupied = occupiedUntil > nowMs;

    return {
      id: spot.id,
      name: spot.name || displayType,
      latitude: spot.location.latitude,
      longitude: spot.location.longitude,
      score: spot.aiResult?.score || spot.score || 5,
      spotType: displayType,
      distanceMeters: Math.round(distance),
      isDriveable: spot.spotType !== 'coastal' && spot.spotType !== 'attraction',
      legalStatus: spot.aiResult?.anbefaling || 'SJEKK_SKILT',
      elevation: spot.elevation ?? spot.aiResult?.elevation_moh ?? 0,
      nearestHouse: spot.nearestHouse ?? 0,
      hasBarrier: spot.legal?.hasBarrier ?? false,
      description: description.slice(0, 150),
      imageUrl: allImages[0] || '',
      temperature: (spot as any).weather?.temp ?? 0,
      weatherSymbol: (spot as any).weather?.symbol ?? '',
      badges: (spot as any).badges || [],
      isVerified: spot.isVerified ?? false,
      occupiedByName: stillOccupied ? (spot.occupiedByName ?? '') : '',
      occupiedUntilMs: stillOccupied ? occupiedUntil : 0,
      sunriseMs,
      sunsetMs,
    };
  });

  try {
    await FricampCar.updateNearbySpots({ spots: payload });
    console.log('[AndroidAuto] updateNearbySpots SUCCESS');
  } catch (e) {
    console.error('[AndroidAuto] updateNearbySpots FAILED:', e);
  }
}

/**
 * Send current user position to Android Auto.
 */
export async function sendPositionToAuto(
  position: Coordinates,
  heading = 0,
  speed = 0,
): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.updatePosition({
      latitude: position.latitude,
      longitude: position.longitude,
      heading,
      speed,
    });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to send position:', e);
  }
}

/**
 * Send active route to Android Auto display.
 *
 * If you have OSRM-style turn-by-turn steps from the phone-side router, pass
 * them in `steps` so the car's routing card can show maneuver instructions.
 * Otherwise the car-side will fall back to fetching its own steps from OSRM
 * using the polyline endpoints.
 */
export async function sendRouteToAuto(
  coordinates: [number, number][],
  distanceMeters: number,
  durationSeconds: number,
  destinationName: string,
  steps?: CarRouteStepPayload[],
  destinationSpotId?: string,
): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.showRoute({
      route: { coordinates, distanceMeters, durationSeconds, destinationName, steps, destinationSpotId },
    });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to send route:', e);
  }
}

/**
 * Clear active route on Android Auto.
 */
export async function clearAutoRoute(): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.clearRoute();
  } catch (e) {
    console.warn('[AndroidAuto] Failed to clear route:', e);
  }
}

/**
 * Clear everything currently displayed on the car: spots and route. Used when
 * the user loses Premium access mid-session so the car screen doesn't keep
 * showing stale data after the paywall snaps shut.
 */
export async function clearAutoData(): Promise<void> {
  if (!FricampCar) return;
  try {
    await Promise.all([
      FricampCar.updateNearbySpots({ spots: [] }),
      FricampCar.clearRoute(),
    ]);
  } catch (e) {
    console.warn('[AndroidAuto] Failed to clear car data:', e);
  }
}

/**
 * Tell the native plugin whether the user has Premium access. Native-side
 * defense-in-depth — the TS hook is the primary gate, but this prevents any
 * stray plugin call from leaking data to a non-premium car session.
 */
export async function setAutoSubscriptionGate(hasAccess: boolean): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.setSubscriptionGate({ hasAccess });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to set subscription gate:', e);
  }
}

/**
 * Set language on Android Auto (follows app language).
 */
export async function setAutoLanguage(language: string): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.setLanguage({ language });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to set language:', e);
  }
}

/**
 * Set map style on Android Auto.
 */
export async function setAutoMapStyle(style: 'topo' | 'satellite'): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.setMapStyle({ style });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to set map style:', e);
  }
}

/**
 * Listen for events from Android Auto (spot selected, navigate request).
 */
export function onAutoSpotSelected(handler: (spotId: string) => void): (() => void) | null {
  if (!FricampCar) return null;
  let cleanup: (() => void) | null = null;
  FricampCar.addListener('spotSelected', (data) => handler(data.spotId))
    .then(l => { cleanup = l.remove; });
  return () => cleanup?.();
}

export function onAutoNavigateRequest(handler: (spotId: string) => void): (() => void) | null {
  if (!FricampCar) return null;
  let cleanup: (() => void) | null = null;
  FricampCar.addListener('navigateToSpot', (data) => handler(data.spotId))
    .then(l => { cleanup = l.remove; });
  return () => cleanup?.();
}

export function onAutoConnectionChanged(handler: (connected: boolean) => void): (() => void) | null {
  if (!FricampCar) return null;
  let cleanup: (() => void) | null = null;
  FricampCar.addListener('connectionChanged', (data) => handler(data.connected))
    .then(l => { cleanup = l.remove; });
  return () => cleanup?.();
}

/**
 * Show a check-in prompt on the Android Auto car display.
 */
export async function showAutoCheckInPrompt(spotId: string, spotName: string): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.showCheckInPrompt({ spotId, spotName });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to show check-in prompt:', e);
  }
}

/**
 * Listen for check-in confirmation from Android Auto.
 */
export function onAutoCheckInConfirmed(handler: (spotId: string) => void): (() => void) | null {
  if (!FricampCar) return null;
  let cleanup: (() => void) | null = null;
  FricampCar.addListener('checkInConfirmed', (data) => handler(data.spotId))
    .then(l => { cleanup = l.remove; });
  return () => cleanup?.();
}

/**
 * Manually check in to a spot from the car.
 */
export async function checkInToSpot(spotId: string): Promise<void> {
  if (!FricampCar) return;
  try {
    await FricampCar.checkIn({ spotId });
  } catch (e) {
    console.warn('[AndroidAuto] Failed to check in:', e);
  }
}
